import {
  BasicTransformEvent,
  Canvas,
  FabricObject,
  Line,
  Point,
  TPointerEvent,
  TPointerEventInfo,
} from 'fabric';

import { TextEntity } from '../../metaBullets/classes/TextEntity';

import {
  SNAP_LINE_COLOR,
  SNAP_THRESHOLD,
  PENDING_SNAP_LINE_COLOR,
  SHOW_THRESHOLD,
} from './constants';

import {
  getObjectSnapPoints,
  getSnappableObjects,
  SnapLineEnum,
  isSnapLineBlocked,
} from './helpers';

// Simple state management
let activeSnapLines: Line[] = [];
// Track potential snap positions
let pendingSnapX: { value: number; edge: number } | null = null;
let pendingSnapY: { value: number; edge: number } | null = null;

/**
 * Calculate the endpoints for a snap line between two objects
 */
const calculateSnapLineEndpoints = (
  isHorizontal: boolean,
  value: number,
  sourceObj: FabricObject,
  targetObj: FabricObject,
): { start: Point; end: Point } => {
  // Get bounding boxes that account for rotation and scaling
  const sourceBoundingRect = sourceObj.getBoundingRect();
  const targetBoundingRect = targetObj.getBoundingRect();

  if (isHorizontal) {
    // For horizontal lines (y-axis alignment)
    const sourceLeft = sourceBoundingRect.left;
    const sourceRight = sourceLeft + sourceBoundingRect.width;
    const targetLeft = targetBoundingRect.left;
    const targetRight = targetLeft + targetBoundingRect.width;

    // Determine if we're snapping to a top/bottom edge or center
    const sourceTop = sourceBoundingRect.top;
    const sourceCenter = sourceTop + sourceBoundingRect.height / 2;
    const targetTop = targetBoundingRect.top;
    const targetCenter = targetTop + targetBoundingRect.height / 2;

    // Check if this is a center alignment
    if (Math.abs(value - sourceCenter) < 1 || Math.abs(value - targetCenter) < 1) {
      // Center alignment - use the existing side-to-side logic
      if (sourceRight < targetLeft) {
        // Source is completely to the left of target
        return {
          start: new Point(sourceRight, value),
          end: new Point(targetLeft, value),
        };
      } else if (targetRight < sourceLeft) {
        // Target is completely to the left of source
        return {
          start: new Point(targetRight, value),
          end: new Point(sourceLeft, value),
        };
      } else {
        // Objects overlap horizontally - use the outer boundaries
        return {
          start: new Point(Math.min(sourceLeft, targetLeft), value),
          end: new Point(Math.max(sourceRight, targetRight), value),
        };
      }
    } else {
      // For top edge or bottom edge alignment, connect the farthest corners
      const leftmostX = Math.min(sourceLeft, targetLeft);
      const rightmostX = Math.max(sourceRight, targetRight);

      return {
        start: new Point(leftmostX, value),
        end: new Point(rightmostX, value),
      };
    }
  } else {
    // For vertical lines (x-axis alignment)
    const sourceTop = sourceBoundingRect.top;
    const sourceBottom = sourceTop + sourceBoundingRect.height;
    const targetTop = targetBoundingRect.top;
    const targetBottom = targetTop + targetBoundingRect.height;

    // Determine if we're snapping to a left/right edge or center
    const sourceLeft = sourceBoundingRect.left;
    const sourceCenter = sourceLeft + sourceBoundingRect.width / 2;
    const targetLeft = targetBoundingRect.left;
    const targetCenter = targetLeft + targetBoundingRect.width / 2;

    // Check if this is a center alignment
    if (Math.abs(value - sourceCenter) < 1 || Math.abs(value - targetCenter) < 1) {
      // Center alignment - use the existing top-to-bottom logic
      if (sourceBottom < targetTop) {
        // Source is completely above target
        return {
          start: new Point(value, sourceBottom),
          end: new Point(value, targetTop),
        };
      } else if (targetBottom < sourceTop) {
        // Target is completely above source
        return {
          start: new Point(value, targetBottom),
          end: new Point(value, sourceTop),
        };
      } else {
        // Objects overlap vertically - use the outer boundaries
        return {
          start: new Point(value, Math.min(sourceTop, targetTop)),
          end: new Point(value, Math.max(sourceBottom, targetBottom)),
        };
      }
    } else {
      // For left edge or right edge alignment, connect the farthest corners
      const topmostY = Math.min(sourceTop, targetTop);
      const bottommostY = Math.max(sourceBottom, targetBottom);

      return {
        start: new Point(value, topmostY),
        end: new Point(value, bottommostY),
      };
    }
  }
};

/**
 * Shows a snap guideline
 */
const showSnapLine = (
  canvas: Canvas,
  startPoint: Point,
  endPoint: Point,
  isHorizontal: boolean,
  isBestSnap: boolean | null = false,
): Line => {
  // Create new snap line
  const snapLine = new Line([startPoint.x, startPoint.y, endPoint.x, endPoint.y], {
    strokeWidth: 1,
    stroke: isBestSnap ? PENDING_SNAP_LINE_COLOR : SNAP_LINE_COLOR,
    selectable: false,
    evented: false,
    excludeFromExport: true,
    name: isHorizontal ? SnapLineEnum.SnapLineX : SnapLineEnum.SnapLineY,
    objectCaching: false,
    zIndex: 9999999999999,
    layer: 'overlay',
  });

  canvas.add(snapLine);
  activeSnapLines.push(snapLine);

  return snapLine;
};

/**
 * Clears all active snap lines
 */
const clearSnapLines = (canvas: Canvas): void => {
  activeSnapLines.forEach((line) => {
    canvas.remove(line);
  });
  activeSnapLines = [];
};

/**
 * Process snap points between target and other objects
 */
const processSnapPoints = (
  targetPoints: number[],
  objPoints: number[],
  object: FabricObject,
  threshold: number,
): Array<{ value: number; edge: number; sourceObj: FabricObject; distance: number }> => {
  const snaps: Array<{ value: number; edge: number; sourceObj: FabricObject; distance: number }> =
    [];

  objPoints.forEach((objPoint) => {
    targetPoints.forEach((targetPoint, targetIndex) => {
      const snapDistance = Math.abs(targetPoint - objPoint);
      if (snapDistance < threshold) {
        snaps.push({
          value: objPoint,
          edge: targetIndex,
          sourceObj: object,
          distance: snapDistance,
        });
      }
    });
  });

  return snaps;
};

/**
 * Find the best snap point from a collection of potential snaps
 * Returns the snap point closest to the target object
 */
const findBestSnap = (
  potentialSnaps: Array<{ value: number; edge: number; sourceObj: FabricObject; distance: number }>,
  threshold: number = SNAP_THRESHOLD,
): { value: number; edge: number; sourceObj: FabricObject } | null => {
  if (!potentialSnaps.length) return null;

  // Group by edge (0=top/left, 1=middle/center, 2=bottom/right)
  const snapsByEdge = potentialSnaps.reduce<Record<number, typeof potentialSnaps>>((acc, snap) => {
    if (!acc[snap.edge]) acc[snap.edge] = [];
    acc[snap.edge].push(snap);
    return acc;
  }, {});

  // Priority for snapping: first edges (0, 2), then centers/middles (1)
  const edgePriority = [0, 2, 1];

  // Try to find snaps within threshold by edge priority
  for (const edge of edgePriority) {
    const edgeSnaps = snapsByEdge[edge];
    if (!edgeSnaps) continue;

    // Filter and sort by distance
    const snappablePoints = edgeSnaps.filter((snap) => snap.distance < threshold);
    snappablePoints.sort((a, b) => a.distance - b.distance);

    if (snappablePoints.length > 0) {
      return snappablePoints[0];
    }
  }

  // If no edge-based snaps found, fall back to closest overall snap
  const allSnapsFiltered = potentialSnaps
    .filter((snap) => snap.distance < threshold)
    .sort((a, b) => a.distance - b.distance);

  return allSnapsFiltered.length > 0 ? allSnapsFiltered[0] : null;
};

/**
 * Group snap info objects by their position value
 * This helps to identify collinear snap lines
 */
const groupSnapsByPosition = (
  snapInfoList: Array<{ value: number; edge: number; sourceObj: FabricObject; distance: number }>,
) => {
  const grouped = new Map<
    number,
    Array<{ value: number; edge: number; sourceObj: FabricObject; distance: number }>
  >();

  snapInfoList.forEach((snapInfo) => {
    // Round to a small precision to handle floating point comparison
    const roundedValue = Math.round(snapInfo.value * 100) / 100;

    if (!grouped.has(roundedValue)) {
      grouped.set(roundedValue, []);
    }

    grouped.get(roundedValue)!.push(snapInfo);
  });

  return grouped;
};

/**
 * Calculate the combined endpoints for collinear snap lines
 * This creates a single line spanning from the leftmost/topmost point to the rightmost/bottommost point
 */
const calculateCombinedEndpoints = (
  isHorizontal: boolean,
  value: number,
  snapInfos: Array<{ value: number; edge: number; sourceObj: FabricObject; distance: number }>,
  target: FabricObject,
): { start: Point; end: Point } => {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  // Process each snap info to find the extents
  snapInfos.forEach((snapInfo) => {
    const endpoints = calculateSnapLineEndpoints(isHorizontal, value, snapInfo.sourceObj, target);

    if (isHorizontal) {
      // For horizontal lines, we need min/max X
      minX = Math.min(minX, endpoints.start.x, endpoints.end.x);
      maxX = Math.max(maxX, endpoints.start.x, endpoints.end.x);
    } else {
      // For vertical lines, we need min/max Y
      minY = Math.min(minY, endpoints.start.y, endpoints.end.y);
      maxY = Math.max(maxY, endpoints.start.y, endpoints.end.y);
    }
  });

  if (isHorizontal) {
    return {
      start: new Point(minX, value),
      end: new Point(maxX, value),
    };
  } else {
    return {
      start: new Point(value, minY),
      end: new Point(value, maxY),
    };
  }
};

/**
 * Handle snap guidelines for object movement
 */
export const handleGuideSnap = (
  e: BasicTransformEvent<TPointerEvent> & { target: FabricObject },
  canvas: Canvas,
) => {
  // Exit early if snapping is disabled or no target
  if (!e.target) return false;

  const target = e.target;

  // Не показываем snapping lines для текстовых сущностей из метабуллитов
  if (TextEntity.isTextEntity(target) && (target as TextEntity).metaBulletId) {
    return false;
  }
  const targetSnapPoints = getObjectSnapPoints(target);
  const snappableObjects = getSnappableObjects(canvas, target);

  // Clear existing snap lines
  clearSnapLines(canvas);

  // Store all potential snap points
  let allPotentialVerticalSnaps: Array<{
    value: number;
    edge: number;
    sourceObj: FabricObject;
    distance: number;
  }> = [];
  let allPotentialHorizontalSnaps: Array<{
    value: number;
    edge: number;
    sourceObj: FabricObject;
    distance: number;
  }> = [];

  // Collect potential snap points using the SHOW_THRESHOLD
  snappableObjects.forEach((object) => {
    const objPoints = getObjectSnapPoints(object);

    const verticalSnaps = processSnapPoints(
      targetSnapPoints.vertical,
      objPoints.vertical,
      object,
      SHOW_THRESHOLD,
    );
    allPotentialVerticalSnaps = [...allPotentialVerticalSnaps, ...verticalSnaps];

    const horizontalSnaps = processSnapPoints(
      targetSnapPoints.horizontal,
      objPoints.horizontal,
      object,
      SHOW_THRESHOLD,
    );
    allPotentialHorizontalSnaps = [...allPotentialHorizontalSnaps, ...horizontalSnaps];
  });

  // Find best snap points using the strict SNAP_THRESHOLD
  const bestSnapX = findBestSnap(allPotentialVerticalSnaps);
  const bestSnapY = findBestSnap(allPotentialHorizontalSnaps);

  // Group vertical snaps by position value
  const groupedVerticalSnaps = groupSnapsByPosition(allPotentialVerticalSnaps);

  // Draw vertical snap lines - one per unique position
  groupedVerticalSnaps.forEach((snaps, value) => {
    // Skip if all snap lines in this group would be blocked
    const allBlocked = snaps.every((snapInfo) => {
      const endpoints = calculateSnapLineEndpoints(
        false,
        snapInfo.value,
        snapInfo.sourceObj,
        target,
      );
      return isSnapLineBlocked(
        canvas,
        snapInfo.sourceObj,
        target,
        endpoints.start,
        endpoints.end,
        false,
      );
    });

    if (allBlocked) return;

    // Calculate combined endpoints for all snaps at this position
    const endpoints = calculateCombinedEndpoints(false, value, snaps, target);

    // Check if this snap aligns with the best snap
    const isBestSnap = bestSnapX && Math.abs(value - bestSnapX.value) < 0.1;

    // Draw a single consolidated line
    showSnapLine(canvas, endpoints.start, endpoints.end, false, isBestSnap);
  });

  // Group horizontal snaps by position value
  const groupedHorizontalSnaps = groupSnapsByPosition(allPotentialHorizontalSnaps);

  // Draw horizontal snap lines - one per unique position
  groupedHorizontalSnaps.forEach((snaps, value) => {
    // Skip if all snap lines in this group would be blocked
    const allBlocked = snaps.every((snapInfo) => {
      const endpoints = calculateSnapLineEndpoints(
        true,
        snapInfo.value,
        snapInfo.sourceObj,
        target,
      );
      return isSnapLineBlocked(
        canvas,
        snapInfo.sourceObj,
        target,
        endpoints.start,
        endpoints.end,
        true,
      );
    });

    if (allBlocked) return;

    // Calculate combined endpoints for all snaps at this position
    const endpoints = calculateCombinedEndpoints(true, value, snaps, target);

    // Check if this snap aligns with the best snap
    const isBestSnap = bestSnapY && Math.abs(value - bestSnapY.value) < 0.1;

    // Draw a single consolidated line
    showSnapLine(canvas, endpoints.start, endpoints.end, true, isBestSnap);
  });

  // Store pending snaps for application later - only store if within SNAP_THRESHOLD
  pendingSnapX = bestSnapX;
  pendingSnapY = bestSnapY;

  target.setCoords();
  canvas.renderAll();
};

/**
 * Apply snap adjustments when mouse up occurs
 */
export const applySnap = (
  e: TPointerEventInfo<TPointerEvent> & {
    isClick: boolean;
    currentTarget?: FabricObject;
    currentSubTargets: FabricObject[];
  },
  canvas: Canvas,
) => {
  if (!e.target) return false;

  const target = e.target;
  const targetSnapPoints = getObjectSnapPoints(target);
  let hasSnapped = false;

  // Apply horizontal snap if exists
  if (pendingSnapY !== null) {
    const snapOffsetY = pendingSnapY.value - targetSnapPoints.horizontal[pendingSnapY.edge];
    target.set('top', target.top + snapOffsetY);
    hasSnapped = true;
  }

  // Apply vertical snap if exists
  if (pendingSnapX !== null) {
    const snapOffsetX = pendingSnapX.value - targetSnapPoints.vertical[pendingSnapX.edge];
    target.set('left', target.left + snapOffsetX);
    hasSnapped = true;
  }

  // Clear pending snaps
  pendingSnapX = null;
  pendingSnapY = null;

  // Update object and canvas
  if (hasSnapped) {
    target.fire('moving');
    target.setCoords();
    canvas.renderAll();
  }

  return hasSnapped;
};

/**
 * Set up event handlers for snapping
 */
export const setupSnappingHandlers = (
  canvas: Canvas,
  sideEffects?: {
    onSnapEnd?: () => void;
  },
): void => {
  // Throttle move events
  let lastMoveTime = 0;
  const moveThrottleTime = 16; // ~60fps

  canvas.on(
    'object:moving',
    (
      e: BasicTransformEvent<TPointerEvent> & {
        target: FabricObject;
      },
    ) => {
      const currentTime = Date.now();
      if (currentTime - lastMoveTime < moveThrottleTime) return;

      lastMoveTime = currentTime;

      // Clear existing snap lines first
      clearSnapLines(canvas);

      // Only show snap lines if Alt key is not pressed
      if (!e.e.altKey && !e.e.shiftKey) {
        handleGuideSnap(e, canvas);
      }
    },
  );

  // Apply snap on mouse up
  canvas.on(
    'mouse:up',
    (
      e: TPointerEventInfo<TPointerEvent> & {
        isClick: boolean;
        currentTarget?: FabricObject;
        currentSubTargets: FabricObject[];
      },
    ) => {
      if (e.target && !e.e.altKey && !e.e.shiftKey) {
        applySnap(e, canvas);
        sideEffects?.onSnapEnd?.();
      }
      // Always clear snap lines on mouse up
      clearSnapLines(canvas);
    },
  );

  // Handle selection cleared - remove snap lines and reset state
  canvas.on('selection:cleared', () => {
    clearSnapLines(canvas);
    pendingSnapX = null;
    pendingSnapY = null;
  });

  // TODO:
  // Also clear snap lines when key is pressed (for Alt key toggling)
  // canvas.on('key:down', (e) => {
  //   if (e.e.key === 'Alt') {
  //     clearSnapLines(canvas);
  //   }
  // });
};
